import angular from "angular";
import $ from "jquery";
import "bootstrap";
import { chunk, cloneDeep } from "lodash";
import { parseTagIds, parseTagIdFile } from "./device-tag-id-parser";

const DEVICES_PER_PAGE = 100;
// The API accepts up to 1000 devices per request
const IMPORT_CHUNK_SIZE = 500;
const IMPORT_FATAL_STATUSES = [401, 403, 404];
const MOVE_CONFIRM_MODAL = '#deviceGroupMoveConfirmModal';

angular
    .module('ui-sentinel.devicegroups')
    .directive('devicegroupDevices', DeviceGroupDevicesDirective);

function DeviceGroupDevicesDirective() {
    var directive = {
        restrict: 'A',
        controller: ThisDirectiveController,
        controllerAs: 'deviceGroupDevices',
        template: require('./devicegroup-devices-directive.html'),
        link: link
    };
    return directive;

    function link(scope, element, attrs, controller) {
        scope.$watch(
            function (scope) {
                return scope.deviceGroupAdmin.deviceGroup;
            },
            function (newValue, oldValue) {
                controller.deviceGroup = newValue;
            }, true
        );
    }
}

ThisDirectiveController.$inject = ['$scope', '$rootScope', '$state', '$timeout', 'DeviceGroupsService', 'DevicesService', 'SentinelUiSession', 'FeedbackService', 'ApiErrorService'];

function ThisDirectiveController($scope, $rootScope, $state, $timeout, DeviceGroupsService, DevicesService, SentinelUiSession, FeedbackService, ApiErrorService) {
    var filteredDevices = [];
    var fileInputElement = null;
    var vm = {
        feedback: FeedbackService,
        deviceGroup: null,
        devices: [],
        pageDevices: [],
        deviceFilter: '',
        pager: {
            currentPage: 1,
            maxPage: 1,
            totalCount: 0,
            firstRow: 0,
            goToPage: goToPage
        },
        importProgress: null,
        otherGroups: null,
        // The group import/export awaiting the user's OK in the confirmation dialog:
        // { title, action, fromGroup, toGroup, deviceCount, verb }
        moveConfirmation: null,
        panel: {
            isCollapsed: true,
            toggle: function () {
                this.isCollapsed = !this.isCollapsed;
                if (!this.isCollapsed) {
                    load();
                }
            }
        },
        mode: {
            isMoving: this.isImportingGroup || this.isExportingGroup || this.isImportingDevices,
            isImportingGroup: false,
            isExportingGroup: false,
            isImportingDevices: false
        },
        form: {
            isPristine: true,
            deviceTagIds: null,
            tagIdCount: 0,
            duplicateCount: 0,
            duplicatesRemoved: false,
            errors: {
                isBlank: true
            }
        },
        hasPermission: {
            toMove: false
        },
        actions: {
            importDevices: importDevices,
            cancelImport: cancelImport,
            finishImport: finishImport,
            onTagIdsChange: onTagIdsChange,
            onTagIdsPaste: onTagIdsPaste,
            removeDuplicateTagIds: removeDuplicateTagIds,
            onFileChange: onFileChange,
            filterDevices: filterDevices,
            beginDevicesImport: beginDevicesImport,
            beginGroupImport: beginGroupImport,
            endMoves: endMoves,
            importGroup: importGroup,
            beginGroupExport: beginGroupExport,
            exportGroup: exportGroup,
            confirmMove: confirmMove,
            cancelMove: cancelMove,
            goToDevice: goToDevice,
            reset: reset,
            reload: load
        }
    };
    activate();
    return vm;

    function activate() {
        setPermissions();
    }

    function importDevices() {
        vm.feedback.clear();
        vm.form.isPristine = false;

        var tagIds = parseTagIds(vm.form.deviceTagIds).ids;

        vm.form.errors.isBlank = tagIds.length === 0;
        if (vm.form.errors.isBlank) {
            return;
        }

        vm.importProgress = {
            total: tagIds.length,
            done: 0,
            added: 0,
            alreadyInGroup: [],
            notAssigned: [],
            failed: [],
            notProcessed: [],
            notAssignedText: '',
            failedText: '',
            notProcessedText: '',
            errorMessage: null,
            isRunning: true,
            isCancelled: false
        };

        importChunks(chunk(tagIds, IMPORT_CHUNK_SIZE), 0);
    }

    function importChunks(chunks, index) {
        var progress = vm.importProgress;

        if (index >= chunks.length || progress.isCancelled) {
            progress.notProcessed = chunks.slice(index).flat();
            progress.notAssignedText = progress.notAssigned.join('\n');
            progress.failedText = progress.failed.join('\n');
            progress.notProcessedText = progress.notProcessed.join('\n');
            progress.isRunning = false;
            return;
        }

        var batch = chunks[index];
        var promise = DeviceGroupsService.addDevicesToGroup(SentinelUiSession.focus, vm.deviceGroup, batch).$promise;
        promise.then(
            function (result) {
                // Read the whole response before touching the progress so a bad shape leaves the counts consistent
                const alreadyInGroup = result.alreadyInGroup || [];
                const notAssigned = result.notAssigned || [];
                const failed = (result.failed || []).map(failure => failure.deviceTagId);
                progress.added += result.added || 0;
                progress.alreadyInGroup.push(...alreadyInGroup);
                progress.notAssigned.push(...notAssigned);
                progress.failed.push(...failed);
                progress.done += batch.length;
                importChunks(chunks, index + 1);
            },
            function (error) {
                // These would fail the same way for every remaining chunk
                if (IMPORT_FATAL_STATUSES.includes(error.status)) {
                    progress.errorMessage = ApiErrorService.getMessage(error, 'The devices could not be imported.');
                    progress.isCancelled = true;
                    importChunks(chunks, index);
                    return;
                }

                progress.failed.push(...batch);
                progress.done += batch.length;
                importChunks(chunks, index + 1);
            }
        ).catch(function () {
            // Something unexpected (bad response shape etc.) - stop so the user is not left on "Importing..." forever
            if (progress.isRunning) {
                progress.errorMessage = progress.errorMessage || 'The device import stopped unexpectedly.';
                progress.isCancelled = true;
                importChunks(chunks, index);
            }
        });
    }

    // Takes effect once the chunk in flight has finished
    function cancelImport() {
        vm.importProgress.isCancelled = true;
    }

    function finishImport() {
        load();
        endMoves();
    }

    function onTagIdsChange() {
        var parsed = parseTagIds(vm.form.deviceTagIds);
        vm.form.tagIdCount = parsed.ids.length;
        vm.form.duplicateCount = parsed.duplicateCount;
        vm.form.duplicatesRemoved = false;
        vm.form.isPristine = true;
    }

    // The paste event fires before the textarea's model is updated
    function onTagIdsPaste() {
        $timeout(removeDuplicateTagIds);
    }

    // Rewrites the textarea without the duplicates, one tag id per line. Done on paste, on blur and on
    // file load rather than on every keystroke, so the cursor is not moved while the user is typing.
    function removeDuplicateTagIds() {
        var parsed = parseTagIds(vm.form.deviceTagIds);
        if (parsed.duplicateCount === 0) {
            return;
        }
        vm.form.deviceTagIds = parsed.ids.join('\n');
        vm.form.tagIdCount = parsed.ids.length;
        vm.form.duplicateCount = parsed.duplicateCount;
        vm.form.duplicatesRemoved = true;
    }

    function onFileChange(files, fileInput) {
        var file = files && files[0];
        fileInputElement = fileInput;
        if (!file) {
            return;
        }

        var reader = new FileReader();
        reader.onload = function () {
            var parsed = parseTagIdFile(reader.result);
            vm.form.deviceTagIds = parsed.ids.join('\n');
            vm.form.tagIdCount = parsed.ids.length;
            vm.form.duplicateCount = parsed.duplicateCount;
            vm.form.duplicatesRemoved = true;
            vm.form.isPristine = true;
            $scope.$apply();
        };
        reader.onerror = function () {
            vm.feedback.addError('The file ' + file.name + ' could not be read.');
            fileInput.value = '';
            $scope.$apply();
        };
        reader.readAsText(file);
    }

    function filterDevices() {
        var filter = (vm.deviceFilter || '').trim().toLowerCase();

        filteredDevices = filter ?
            vm.devices.filter(device => (device.imei || '').toLowerCase().includes(filter)) :
            vm.devices;

        vm.pager.totalCount = filteredDevices.length;
        vm.pager.maxPage = Math.max(1, Math.ceil(filteredDevices.length / DEVICES_PER_PAGE));
        goToPage(1);
    }

    function goToPage(page) {
        vm.pager.currentPage = page;
        vm.pager.firstRow = (page - 1) * DEVICES_PER_PAGE;
        vm.pageDevices = filteredDevices.slice(vm.pager.firstRow, vm.pager.firstRow + DEVICES_PER_PAGE);
    }

    function beginDevicesImport() {
        reset();
        vm.mode.isImportingDevices = true;
    }

    function beginGroupImport() {
        loadOtherGroups();
        vm.mode.isImportingGroup = true;
    }

    function beginGroupExport() {
        loadOtherGroups();
        vm.mode.isExportingGroup = true;
    }

    function endMoves() {
        vm.mode.isImportingGroup = false;
        vm.mode.isExportingGroup = false;
        vm.mode.isImportingDevices = false;
        vm.form.devices = null;
        vm.importProgress = null;
        vm.otherGroups = [];
    }

    function goToDevice(device) {
        const returnState = $state.current.name;
        const returnStateParams = cloneDeep($state.params);
        $state.go('device.admin', {
            deviceTagId: device.imei,
            referrer: returnState,
            referrerParams: returnStateParams
        });
    }

    function load() {
        vm.devices = [];
        filterDevices();
        $rootScope.loading = true;
        var promise = DeviceGroupsService.getDevices(SentinelUiSession.focus, vm.deviceGroup).$promise;
        promise.then(
            function (result) {
                vm.devices = result;
                filterDevices();
                $rootScope.loading = false;
            },
            function (error) {
                vm.feedback.addErrorFromError(error);
                $rootScope.loading = false;
            }
        );
    }

    function loadOtherGroups() {
        vm.otherGroups = [];
        $rootScope.loading = true;
        var promise = (SentinelUiSession.user.accountId == SentinelUiSession.focus.id) ?
            DeviceGroupsService.getGroups(SentinelUiSession.focus).$promise :
            DeviceGroupsService.getGroupsByAccountId(SentinelUiSession.focus).$promise;
        promise.then(
            function (result) {
                $rootScope.loading = false;
                var groups = [];

                _.forEach(result, function (group) {
                    if (group.id !== vm.deviceGroup.id) {
                        var extGroup = angular.extend(group, {
                            deviceCount: group.totalDevices,
                            forwarding: null
                        });

                        groups.push(extGroup);

                        var fwdPromise = DeviceGroupsService.getForwarding(SentinelUiSession.focus, extGroup).$promise;
                        fwdPromise.then(
                            function (result) {
                                extGroup.forwarding = result;
                            },
                            function (error) {
                            }
                        );
                    }
                });
                vm.otherGroups = groups;
            },
            function (error) {
                $rootScope.loading = false;
                vm.feedback.addErrorFromError(error);
            }
        );
    }

    // Importing or exporting moves every device of a group, so the user confirms it first.
    function importGroup(group) {
        askToConfirmMove({
            title: 'Group Import',
            action: 'Import',
            fromGroup: group,
            toGroup: vm.deviceGroup,
            deviceCount: group.deviceCount,
            verb: 'imported into'
        });
    }

    function exportGroup(group) {
        askToConfirmMove({
            title: 'Group Export',
            action: 'Export',
            fromGroup: vm.deviceGroup,
            toGroup: group,
            deviceCount: vm.devices.length,
            verb: 'exported to'
        });
    }

    function askToConfirmMove(confirmation) {
        vm.moveConfirmation = confirmation;
        $(MOVE_CONFIRM_MODAL).modal('show');
    }

    function confirmMove() {
        const confirmation = vm.moveConfirmation;
        vm.moveConfirmation = null;
        $(MOVE_CONFIRM_MODAL).modal('hide');
        if (confirmation) {
            moveAllDevices(confirmation.fromGroup, confirmation.toGroup, confirmation.deviceCount, confirmation.verb);
        }
    }

    function cancelMove() {
        vm.moveConfirmation = null;
    }

    // The whole group is moved in one request, which takes a while for a large group.
    // The API answers with the devices left in the source group, so the number moved is
    // worked out from the source group's count before the move.
    function moveAllDevices(fromGroup, toGroup, fromCountBefore, verb) {
        vm.feedback.clear();
        $rootScope.loading = true;

        var promise = DeviceGroupsService.moveDevices(SentinelUiSession.focus, fromGroup, toGroup).$promise;
        promise.then(
            function (result) {
                var fromCountAfter = Array.isArray(result) ? result.length : 0;
                var moved = typeof fromCountBefore === 'number' ? Math.max(fromCountBefore - fromCountAfter, 0) : null;
                var summary = moved === null ? 'Devices' : moved + (moved === 1 ? ' device' : ' devices');
                vm.feedback.addSuccess(summary + (moved === 1 ? ' was ' : ' were ') + verb + ' ' + toGroup.groupName + ' from ' + fromGroup.groupName);
                load();
                endMoves();
            },
            function (error) {
                $rootScope.loading = false;
                vm.feedback.addErrorFromError(error);
                endMoves();
            }
        );
    }

    function setPermissions() {
        vm.hasPermission.toMove =
            SentinelUiSession.user.isSystemAdmin ||
            SentinelUiSession.user.isAccountAdmin ||
            SentinelUiSession.user.isAccountEditor;
    }

    function reset() {
        vm.form.isPristine = true;
        vm.form.deviceTagIds = null;
        vm.form.errors.isBlank = true;
        vm.form.tagIdCount = 0;
        vm.form.duplicateCount = 0;
        vm.form.duplicatesRemoved = false;
        if (fileInputElement) {
            fileInputElement.value = '';
        }
    }
}
